import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Users, Building } from 'lucide-react';
import { useKYPSearch } from '../../../lib/kyp-search';
import { useAuth } from '../../../lib/auth.context';

export const AgentDetailsSection: React.FC = () => {
  const { playerId, playerProfile } = useKYPSearch();
  const [agentInfo, setAgentInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getToken } = useAuth();
  const fetchAttemptedRef = useRef(false);

  useEffect(() => {
    // Only fetch if we haven't attempted yet and have valid inputs
    if (!fetchAttemptedRef.current && playerId && playerProfile?.agent_name) {
      fetchAttemptedRef.current = true;

      const fetchAgentInfo = async () => {
        try {
          setIsLoading(true);
          setError(null);

          const token = getToken();
          if (!token) {
            setError('Not authenticated');
            setIsLoading(false);
            return;
          }

          const requestHeaders = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };

          // In development, use relative URLs to leverage the Vite proxy
          const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;

          console.log(`Fetching agent info for player ID: ${playerId} from ${baseUrl}/ai/v1/player/${playerId}/agent-info`);
          const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/agent-info`, {
            headers: requestHeaders
          });

          console.log(`Agent info response status: ${response.status}`);

          if (!response.ok) {
            // Try to get more details from the error response
            try {
              const errorData = await response.json();
              console.error('Error response data:', errorData);
              throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.message || 'Unknown error'}`);
            } catch (parseError) {
              // If we can't parse the error response, just throw the status
              throw new Error(`HTTP error! status: ${response.status}`);
            }
          }

          const data = await response.json();
          console.log('Received agent info:', data);
          setAgentInfo(data);
        } catch (error) {
          console.error('Error fetching agent info:', error);
          // Check if it's a 404 error, which might mean the endpoint isn't implemented yet
          if (error.message && error.message.includes('status: 404')) {
            setError('Agent details not available');
          } else {
            setError('Failed to load agent information');
          }
        } finally {
          setIsLoading(false);
        }
      };

      fetchAgentInfo();
    }
  }, [playerId, playerProfile, getToken]);

  // Reset fetch attempted flag when inputs change
  useEffect(() => {
    if (playerId) {
      fetchAttemptedRef.current = false;
    }
  }, [playerId]);

  if (!playerId || !playerProfile?.agent_name) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-blue-500 mr-2" />
          <span className="text-sm text-gray-500">Loading agent information...</span>
        </div>
      </div>
    );
  }

  if (error) {
    // If the error is about the endpoint not being available, just return null
    // to avoid showing an error message for a feature that might not be fully implemented
    if (error === 'Agent details not available') {
      return null;
    }

    return (
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-500">Agent details temporarily unavailable</p>
      </div>
    );
  }

  if (!agentInfo) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-500">No agent information available</p>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Agent Details</h4>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">{agentInfo.agent_name}</p>
          <div className="flex items-center space-x-4">
            <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
              <Users className="h-3.5 w-3.5 mr-1" />
              <span>{agentInfo.total_players_count} players</span>
            </div>
            <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
              <Building className="h-3.5 w-3.5 mr-1" />
              <span>{agentInfo.total_clubs_count} clubs</span>
            </div>
          </div>
        </div>

        {agentInfo.players_represented && agentInfo.players_represented.length > 0 && (
          <div className="mt-2">
            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Other players represented:</h5>
            <div className="max-h-40 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="px-2 py-1 text-left text-gray-700 dark:text-gray-300">Player</th>
                    <th className="px-2 py-1 text-left text-gray-700 dark:text-gray-300">Club</th>
                    <th className="px-2 py-1 text-left text-gray-700 dark:text-gray-300">Position</th>
                    <th className="px-2 py-1 text-right text-gray-700 dark:text-gray-300">Market Value</th>
                  </tr>
                </thead>
                <tbody>
                  {agentInfo.players_represented.map((player) => (
                    <tr key={player.id} className="border-b border-gray-200 dark:border-gray-700">
                      <td className="px-2 py-1 text-gray-800 dark:text-gray-200">{player.name}</td>
                      <td className="px-2 py-1 text-gray-600 dark:text-gray-400">{player.club}</td>
                      <td className="px-2 py-1 text-gray-600 dark:text-gray-400">{player.position}</td>
                      <td className="px-2 py-1 text-right text-gray-600 dark:text-gray-400">{player.market_value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};